import { useTheme } from '@emotion/react';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router';
import { getUser, getUserToken } from '@/redux/user';

import { ChannelId } from '@/services/innovorder/channel/channel.type';
import { ActionBar } from '@/components/atoms/ActionBar';
import { Text } from '@/components/atoms/Text';
import { NavigationContext, NavigationParametersEnum, Routes } from '@/core/router/routes.types';
import {
    useCancelOrderMutation,
    useGetOrderDetailsQuery,
    useGetOrderHistoryQuery,
    useGetTransactionsByOrderQuery,
    useRefundOrderMutation,
    useCancelOrderWithContraEntryMutation,
} from '@/services/innovorder/orders/order.endpoints';
import Tabs from '@/components/organisms/Tabs/Tabs';
import Tab from '@/components/organisms/Tabs/Tab';
import { stringifyError } from '@/utils/errors';
import { formatDate } from '@/utils/date';
import { CancelOrderReason } from '@/services/innovorder/orders/order.enum';
import { SvyButton } from '@/components/savory/SvyButton';
import { ManualPaymentMethodCode, OmnichannelOrder } from '@/services/innovorder/orders/order.type';
import { PaymentMethodCode } from '@/services/innovorder/paymentMethod/paymentMethod.type';
import Card from '../../../components/molecules/Card';
import Loader from '../../../components/atoms/Loader';
import {
    Container,
    DetailContainer,
    MainDetailContainer,
    SideDetailContainer,
    ActionBarButton,
} from './OrderDetails.style';
import OrderDetailsCancelConfirmation from './OrderDetailsCancelConfirmation';
import OrderDetailsCustomer from './OrderDetailsCustomer';
import OrderDetailsDelivery from './OrderDetailsDelivery';
import OrderDetailsHeader from './OrderDetailsHeader';
import OrderDetailsPayment from './OrderDetailsPayment';
import OrderDetailsPos from './OrderDetailsPos';
import OrderDetailsTax from './OrderDetailsTax';
import OrderDetailsTicket from './OrderDetailsTicket';
import OrderDetailsPhotos from './OrderDetailsPhotos/OrderDetailsPhotos';
import OrderDetailsTransaction from './OrderDetailsTransaction/OrderDetailsTransaction';
import OrderDetailsLinkedOrder from './OrderDetailsLinkedOrder';
import OrderDetailsContraEntry from './OrderDetailsContraEntryInfos';

type ParamTypes = Required<Pick<NavigationContext, NavigationParametersEnum.orderId>>;

const OrderDetails: React.FunctionComponent<React.PropsWithChildren> = () => {
    const intl = useIntl();
    const { orderId } = useParams<ParamTypes>();
    const token = useSelector(getUserToken);
    const user = useSelector(getUser);
    const {
        refetch: refetchOrderDetails,
        data: order,
        isLoading: orderIsLoading,
        error: orderError,
    } = useGetOrderDetailsQuery({ orderId });

    // Fetch order history
    const { data: orderHistory, isLoading: historyIsLoading } = useGetOrderHistoryQuery(
        { orderId, token },
        { skip: !orderId || !token },
    );

    // Combine current order with history and sort by date (newest first)
    const allOrders = React.useMemo(() => {
        if (!order) return [];
        const orders = orderHistory ? [...orderHistory] : [];
        // Add current order if not already in history
        const currentOrderInHistory = orders.some((o) => o.omnichannelOrderId === order.omnichannelOrderId);
        if (!currentOrderInHistory) {
            orders.push(order);
        }
        // Sort by lastUpdateDate descending (newest first)
        return orders.sort((a, b) => new Date(b.lastUpdateDate).getTime() - new Date(a.lastUpdateDate).getTime());
    }, [order, orderHistory]);

    // State to track selected order index (0 = newest)
    const [selectedOrderIndex, setSelectedOrderIndex] = useState(0);
    const selectedOrder = allOrders[selectedOrderIndex] || order;

    const [refundOrder, { isLoading: isRefunding, isSuccess: isRefunded, error: refundError }] =
        useRefundOrderMutation();
    const [cancelOrder, { isLoading: isCancelling, isSuccess: isCancelled, error: cancelError }] =
        useCancelOrderMutation();
    const [
        cancelOrderWithContraEntry,
        {
            isLoading: isCancellingWithContraEntry,
            isSuccess: isCancelledWithContraEntry,
            error: cancelErrorWithContraEntry,
        },
    ] = useCancelOrderWithContraEntryMutation();
    const [isCancelModalOpened, setIsCancelModalOpened] = useState(false);

    // Check if we're viewing the current/newest order (index 0 is the newest after sorting by lastUpdateDate)
    // Actions should only be available on the newest version of the order
    const isViewingCurrentOrder = selectedOrderIndex === 0;

    // Action eligibility should only be based on the current order, not historical versions
    const isCancellable = isViewingCurrentOrder ? order?.isCancellable : false;
    const isRefundable = isViewingCurrentOrder ? order?.isRefundable && !order?.isCancellable : false;
    const canCancelOrderWithContraEntry =
        isViewingCurrentOrder &&
        order &&
        user?.accessFlags.CAN_CANCEL_ORDER_WITH_CONTRA_ENTRY &&
        new Date(order.startDate).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0); // true if order from yesterday
    const hasContraEntry = isViewingCurrentOrder ? !!order?.reportOrderContraEntry : false;
    const channelId = isViewingCurrentOrder ? order?.channelId : selectedOrder?.channelId;
    const consumptionModeId = isViewingCurrentOrder ? order?.consumptionModeId : selectedOrder?.consumptionModeId;

    const hasManualPaymentMethodToChoose =
        canCancelOrderWithContraEntry &&
        order?.payments?.some(
            (payment) =>
                payment.type !== PaymentMethodCode.EWallet &&
                payment.type !== PaymentMethodCode.Stripe &&
                payment.type !== PaymentMethodCode.Unknown, // Stripe
        );

    const theme = useTheme();

    useEffect(() => {
        if (isRefunded || isCancelled || isCancelledWithContraEntry) {
            refetchOrderDetails();
        }
    }, [isRefunded, isCancelled, isCancelledWithContraEntry, orderId, token, refetchOrderDetails]);

    const toggleCancelModal = useCallback(() => {
        setIsCancelModalOpened((state) => !state);
    }, []);

    const handleCancelConfirmation = useCallback(
        (
            cancellationReason: CancelOrderReason,
            cancellationReasonDescription?: string,
            manualPaymentMethodCode?: ManualPaymentMethodCode,
        ) => {
            setIsCancelModalOpened(false);
            if (canCancelOrderWithContraEntry) {
                cancelOrderWithContraEntry({
                    token,
                    orderId,
                    cancellationReason,
                    cancellationReasonDescription,
                    manualPaymentMethodCode,
                });
            } else if (isRefundable) {
                refundOrder({ token, orderId, cancellationReason, cancellationReasonDescription, channelId });
            } else if (isCancellable) {
                cancelOrder({ token, orderId, cancellationReason, cancellationReasonDescription, channelId });
            }
        },
        [
            cancelOrder,
            refundOrder,
            cancelOrderWithContraEntry,
            isRefundable,
            isCancellable,
            orderId,
            token,
            channelId,
            canCancelOrderWithContraEntry,
        ],
    );

    // Component to render order details content for a specific order
    const OrderContent: React.FC<{ orderToRender: OmnichannelOrder }> = ({ orderToRender }) => {
        const shouldSkipTransactions = () => {
            if (!orderToRender) return true;
            if (
                Number(orderToRender?.channelId) === ChannelId.WEB ||
                Number(orderToRender?.channelId) === ChannelId.KIOSK
            )
                return false;
            return true;
        };

        const { data: orderTransactions } = useGetTransactionsByOrderQuery(
            {
                orderId: orderToRender.omnichannelOrderId,
            },
            { skip: shouldSkipTransactions() },
        );

        return (
            <>
                <OrderDetailsHeader {...orderToRender} />
                <DetailContainer>
                    <MainDetailContainer>
                        {orderToRender.reportOrderContraEntry && (
                            <Card
                                titleId={`orderDetail.linkedOrder.title.${orderToRender.reportOrderContraEntry.type}`}
                                isExpandable
                                isInitialOpened
                            >
                                <OrderDetailsLinkedOrder {...orderToRender.reportOrderContraEntry} />
                            </Card>
                        )}
                        <Card titleId="orderDetail.ticketInfo" isExpandable isInitialOpened>
                            <OrderDetailsTicket order={orderToRender} />
                        </Card>
                        {Boolean(orderToRender.comment) && (
                            <Card titleId="orderDetail.comment" isExpandable isInitialOpened>
                                <Text text={orderToRender.comment} />
                            </Card>
                        )}
                        {Boolean(orderToRender.comment) === false &&
                            typeof orderToRender.webComment === 'string' &&
                            orderToRender.webComment !== '' && (
                                <Card titleId="orderDetail.comment" isExpandable isInitialOpened>
                                    <Text text={orderToRender.webComment} />
                                </Card>
                            )}
                        {Boolean(orderToRender.taxes) && (
                            <Card titleId="orderDetail.taxInfo" isExpandable isInitialOpened>
                                <OrderDetailsTax taxes={orderToRender.taxes} currency={orderToRender.currency} />
                            </Card>
                        )}
                        {Boolean(orderToRender.payments) && (
                            <Card titleId="orderDetail.paymentInfo" isExpandable isInitialOpened>
                                <OrderDetailsPayment
                                    payments={orderToRender.payments}
                                    currency={orderToRender.currency}
                                />
                            </Card>
                        )}
                    </MainDetailContainer>
                    <SideDetailContainer>
                        {(orderToRender.customer ||
                            typeof orderToRender.userName === 'string' ||
                            orderToRender.phoneNumber) && (
                            <Card titleId="orderDetail.customerInfo" isExpandable isInitialOpened>
                                <OrderDetailsCustomer
                                    userName={orderToRender.userName ?? ''}
                                    firstName={orderToRender.customer?.firstName ?? ''}
                                    lastName={orderToRender.customer?.lastName ?? ''}
                                    email={orderToRender.customer?.email}
                                    phone={orderToRender.customer?.phone ?? orderToRender.phoneNumber ?? ''}
                                    badgeNumber={orderToRender.customer?.badgeNumber}
                                    group={orderToRender.customer?.student?.class}
                                    section={orderToRender.customer?.student?.section}
                                    subSection={orderToRender.customer?.student?.subSection}
                                    pricingRuleCode={orderToRender.customer?.student?.pricingRuleCode}
                                    balanceInformation={orderToRender.customer?.balanceInformation}
                                    currency={orderToRender.currency}
                                />
                            </Card>
                        )}
                        {orderToRender.deliveryAddress && (
                            <Card titleId="orderDetail.deliveryInfo" isExpandable isInitialOpened>
                                <OrderDetailsDelivery {...orderToRender.deliveryAddress} />
                            </Card>
                        )}
                        <Card titleId="orderDetail.posInfo" isExpandable isInitialOpened>
                            <OrderDetailsPos
                                seller={orderToRender.seller}
                                deviceName={orderToRender.deviceName}
                                deviceId={orderToRender.deviceId}
                                restaurant={orderToRender.restaurant.name}
                            />
                        </Card>
                        {orderToRender.reportOrderContraEntry &&
                            orderToRender.reportOrderContraEntry.type === 'contra_entry' && (
                                <Card titleId="orderDetail.contraEntryInfo.title" isExpandable isInitialOpened>
                                    <OrderDetailsContraEntry
                                        {...orderToRender.reportOrderContraEntry}
                                        cancellationReasonDescription={orderToRender.cancellationReasonDescription}
                                    />
                                </Card>
                            )}
                        {orderTransactions && orderTransactions.length > 0 && (
                            <Card titleId="orderDetail.transactionInfo" isExpandable isInitialOpened>
                                <OrderDetailsTransaction transactions={orderTransactions.map((t) => t.id)} />
                            </Card>
                        )}
                        {orderToRender.photos?.length ? (
                            <Card titleId="orderDetail.orderPhotos" isExpandable isInitialOpened>
                                <OrderDetailsPhotos photos={orderToRender.photos} />
                            </Card>
                        ) : null}
                    </SideDetailContainer>
                </DetailContainer>
            </>
        );
    };

    if (orderIsLoading || historyIsLoading) {
        return <Loader color={theme.color.primary} size={40} withContainer />;
    }
    if (!order) {
        return <Redirect to={Routes.NotFound} />;
    }

    // Format tab title: "Transaction v.X (Status)"
    const getTabTitle = (orderItem: OmnichannelOrder, index: number) => {
        const version = allOrders.length - index;
        const status = intl.formatMessage({ id: `orderDetail.tabStatus.${orderItem.status}` });
        return `Transaction v.${version} (${status})`;
    };

    // Format tab subtitle with date
    const getTabSubtitle = (orderItem: OmnichannelOrder) => {
        return formatDate(orderItem.lastUpdateDate, 'DD/MM/YYYY HH:mm');
    };

    return (
        <>
            <Container>
                {allOrders.length > 1 ? (
                    <Tabs selectedTabIndex={selectedOrderIndex} onSelectTab={setSelectedOrderIndex}>
                        {allOrders.map((orderItem, index) => (
                            <Tab
                                key={`${orderItem.omnichannelOrderId}-${index}-${orderItem.lastUpdateDate}`}
                                title={getTabTitle(orderItem, index)}
                                subtitle={getTabSubtitle(orderItem)}
                                component={<OrderContent orderToRender={orderItem} />}
                            />
                        ))}
                    </Tabs>
                ) : (
                    <OrderContent orderToRender={order} />
                )}
            </Container>
            {!hasContraEntry && (isCancellable || isRefundable || canCancelOrderWithContraEntry) && (
                <ActionBar
                    information={intl.formatMessage({ id: 'orderDetail.action.text' })}
                    error={
                        stringifyError(orderError) ||
                        stringifyError(refundError) ||
                        stringifyError(cancelError) ||
                        stringifyError(cancelErrorWithContraEntry)
                    }
                >
                    <ActionBarButton>
                        {canCancelOrderWithContraEntry && (
                            <SvyButton isLoading={isCancellingWithContraEntry} onClick={toggleCancelModal}>
                                <FormattedMessage id="orderDetail.action.cancelWithContraEntry" />
                            </SvyButton>
                        )}
                        {isRefundable && !canCancelOrderWithContraEntry && (
                            <SvyButton isLoading={isRefunding} onClick={toggleCancelModal}>
                                <FormattedMessage id="orderDetail.action.refund" />
                            </SvyButton>
                        )}
                        {isCancellable && !canCancelOrderWithContraEntry && (
                            <SvyButton isLoading={isCancelling} onClick={toggleCancelModal}>
                                <FormattedMessage
                                    id={order?.isRefundable ? 'orderDetail.action.refund' : 'orderDetail.action.cancel'}
                                />
                            </SvyButton>
                        )}
                    </ActionBarButton>
                </ActionBar>
            )}
            {isCancelModalOpened && (
                <OrderDetailsCancelConfirmation
                    cancelWithContraEntry={canCancelOrderWithContraEntry ?? false}
                    hasManualPaymentMethodToChoose={hasManualPaymentMethodToChoose ?? false}
                    defaultManualPaymentMethodCode={order.payments[0]?.type as PaymentMethodCode}
                    channelId={channelId!}
                    restaurantId={order.restaurantId}
                    consumptionModeId={consumptionModeId!}
                    onHide={toggleCancelModal}
                    onValidate={handleCancelConfirmation}
                />
            )}
        </>
    );
};

export default OrderDetails;
